import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE } from "../config";
import { createUploadHandler } from "../utils/cloudinaryUtils";
import { MediaPreview } from "../utils/mediaUtils";
import PaymentRequests from "./PaymentRequests";

const DEFAULT_PRODUCT_DETAILS = {
  dai: "200",
  rong: "85",
  cao: "70",
};

const DEFAULT_PRODUCT_FORM = {
  name: "",
  price: "Liên hệ",
  type: "",
  color: "Nhiều màu",
  size: "Tuỳ chỉnh",
  material: "Bố Nhung, Nhung, Nỉ, Lông cừu,...",
  image: "",
  gallery: "",
  realImages: "",
  reviews: [],
  description: "",
  details: { ...DEFAULT_PRODUCT_DETAILS },
  stock: 0,
  tags: "sofa, sofa phòng khách, ghế sofa, nội thất phòng khách, sofa đẹp, sofa hiện đại, sofa tiện nghi, sofa sang trọng, sofa giá tốt, sofa bền đẹp, sofa chất lượng, ghế bọc nệm, sofa thiết kế, sofa xu hướng",
  isFeatured: false,
};

function productToForm(product) {
  const type = product.type === "Sofa" ? "Sofa Băng" : (product.type || "");
  return {
    name: product.name || "",
    price: product.price || "Liên hệ",
    type: type,
    color: product.color || "Nhiều màu",
    size: product.size || "Tuỳ chỉnh",
    material: product.material || "Bố Nhung, Nhung, Nỉ, Lông cừu,...",
    image: product.image || "",
    gallery: Array.isArray(product.gallery) ? product.gallery.join(", ") : "",
    realImages: (
      Array.isArray(product.realImages)
        ? product.realImages
        : Array.isArray(product.details?.realImages)
          ? product.details.realImages
          : []
    ).join(", "),
    reviews: (
      Array.isArray(product.reviews)
        ? product.reviews
        : Array.isArray(product.details?.reviews)
          ? product.details.reviews
          : []
    ).map((review) => ({
      name: review.name || "",
      rating: Number(review.rating || 5),
      content: review.content || review.comment || "",
      date: review.date || "",
      sourceUrl: review.sourceUrl || review.source || "",
    })),
    description: product.description || "",
    details: {
      ...DEFAULT_PRODUCT_DETAILS,
      ...(product.details && typeof product.details === "object" ? product.details : {}),
    },
    stock: typeof product.stock === "number" ? product.stock : 0,
    tags: Array.isArray(product.tags) ? product.tags.join(", ") : "",
    isFeatured: !!product.isFeatured,
  };
}

export default function AdminApp() {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [loginToken, setLoginToken] = useState("");
  const [authStatus, setAuthStatus] = useState(token ? "checking" : "logged_out");
  const [view, setView] = useState("banners");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loadedViewsRef = useRef(new Set());

  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);

  const [newBanner, setNewBanner] = useState({
    image: "",
    title: "",
    subtitle: "",
    description: "",
    buttonText: "",
    buttonLink: "",
    order: 1,
  });

  const [newProduct, setNewProduct] = useState(DEFAULT_PRODUCT_FORM);

  const API = `${API_BASE}/admin`;
  const { single: handleSingleUpload, multiple: handleMultipleUpload } = createUploadHandler(setIsUploading);

  useEffect(() => {
    document.title = "Admin - Cưng Home";

    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken("");
    setLoginToken("");
    setAuthStatus("logged_out");
    setBanners([]);
    setProducts([]);
    loadedViewsRef.current.clear();
    setEditingProductId(null);
    setNewProduct(DEFAULT_PRODUCT_FORM);
  }, []);

  const resetProductForm = useCallback(() => {
    setEditingProductId(null);
    setNewProduct(DEFAULT_PRODUCT_FORM);
  }, []);

  const validateAdminToken = useCallback(async (candidateToken) => {
    const trimmedToken = candidateToken.trim();
    if (!trimmedToken) return false;

    try {
      const res = await fetch(`${API}/products`, {
        headers: { Authorization: `Bearer ${trimmedToken}` },
      });

      if (!res.ok) return false;

      const data = await res.json();
      if (Array.isArray(data)) {
        setProducts(data);
        loadedViewsRef.current.add("products");
      }
      return true;
    } catch (error) {
      console.error("Admin token validation failed:", error);
      return false;
    }
  }, [API]);

  useEffect(() => {
    if (!token) {
      setAuthStatus("logged_out");
      return;
    }

    let cancelled = false;

    const checkStoredToken = async () => {
      setAuthStatus("checking");
      const isValid = await validateAdminToken(token);

      if (cancelled) return;

      if (isValid) {
        setAuthStatus("authenticated");
      } else {
        logout();
      }
    };

    checkStoredToken();

    return () => {
      cancelled = true;
    };
  }, [token, validateAdminToken, logout]);

  // === FETCH DATA ===
  const fetchBanners = useCallback(async () => {
    const res = await fetch(`${API}/banners`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setBanners(await res.json());
      return true;
    } else if (res.status === 401) {
      logout();
    }
    return false;
  }, [API, token, logout]);

  const fetchProducts = useCallback(async () => {
    const res = await fetch(`${API}/products`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setProducts(await res.json());
      return true;
    } else if (res.status === 401) {
      logout();
    }
    return false;
  }, [API, token, logout]);

  useEffect(() => {
    if (authStatus !== "authenticated" || loadedViewsRef.current.has(view)) return;
    const fetchView = view === "banners" ? fetchBanners : view === "products" ? fetchProducts : null;
    if (!fetchView) return;
    loadedViewsRef.current.add(view);
    fetchView().then((loaded) => {
      if (!loaded) loadedViewsRef.current.delete(view);
    }).catch(() => loadedViewsRef.current.delete(view));
  }, [view, authStatus, fetchBanners, fetchProducts]);

  // === ADD BANNER ===
  const addBanner = async () => {
    if (isUploading) return alert("Ảnh đang được tải lên, vui lòng chờ hoàn tất.");
    if (!newBanner.image) return alert("Vui lòng tải ảnh lên hoặc dán link ảnh");

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/banners`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newBanner),
      });
      if (res.ok) {
        alert("✅ Đã thêm banner!");
        setNewBanner({
          image: "",
          title: "",
          subtitle: "",
          description: "",
          buttonText: "",
          buttonLink: "",
          order: 1,
        });
        fetchBanners();
      } else {
        alert("❌ Lỗi khi thêm banner");
      }
    } catch (error) {
      console.error(error);
      alert("❌ Lỗi kết nối server");
    } finally {
      setIsSubmitting(false);
    }
  };

  // === ADD / EDIT PRODUCT ===
  const saveProduct = async () => {
    if (isUploading) return alert("File đang được tải lên, vui lòng chờ hoàn tất.");

    const detailsObj = newProduct.details && typeof newProduct.details === "object" ? newProduct.details : {};
    const realImages = newProduct.realImages
      ? newProduct.realImages.split(",").map((item) => item.trim()).filter(Boolean)
      : [];
    const reviews = newProduct.reviews
      .filter((review) => review.content?.trim())
      .map((review) => ({
        name: review.name?.trim() || "Khách hàng Cưng Home",
        rating: Math.min(5, Math.max(1, Number(review.rating || 5))),
        content: review.content.trim(),
        date: review.date || "",
        sourceUrl: review.sourceUrl?.trim() || "",
        verified: true,
      }));
    const { realImages: _realImages, reviews: _reviews, ...productFields } = newProduct;

    const payload = {
      ...productFields,
      gallery: newProduct.gallery
        ? newProduct.gallery.split(",").map((x) => x.trim())
        : [],
      tags: newProduct.tags
        ? newProduct.tags.split(",").map((x) => x.trim())
        : [],
      details: {
        ...detailsObj,
        realImages,
        reviews,
      },
    };

    setIsSubmitting(true);
    try {
      const res = await fetch(editingProductId ? `${API}/products/${editingProductId}` : `${API}/products`, {
        method: editingProductId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        alert(editingProductId ? "✅ Đã cập nhật sản phẩm!" : "✅ Đã thêm sản phẩm mới!");
        resetProductForm();
        fetchProducts();
      } else if (res.status === 401) {
        logout();
      } else {
        const error = await res.json().catch(() => ({}));
        alert(`❌ Lỗi khi lưu sản phẩm: ${error.error || res.status}`);
      }
    } catch (error) {
      alert("❌ Lỗi khi lưu sản phẩm");
    } finally {
      setIsSubmitting(false);
    }
  };

  // === AUTH ===
  if (authStatus === "checking") {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-3">
        <h2 className="text-2xl font-bold">Admin Login</h2>
        <p className="text-gray-600">Đang kiểm tra phiên đăng nhập...</p>
      </div>
    );
  }

  if (authStatus !== "authenticated") {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-3">
        <h2 className="text-2xl font-bold">Admin Login</h2>
        <input
          type="password"
          placeholder="Nhập token admin..."
          value={loginToken}
          onChange={(e) => setLoginToken(e.target.value)}
          className="border px-4 py-2 rounded w-64"
        />
        <button
          onClick={async () => {
            setAuthStatus("checking");
            const trimmedToken = loginToken.trim();
            const isValid = await validateAdminToken(trimmedToken);

            if (isValid) {
              localStorage.setItem("token", trimmedToken);
              setToken(trimmedToken);
              setAuthStatus("authenticated");
            } else {
              localStorage.removeItem("token");
              setToken("");
              setAuthStatus("logged_out");
              alert("Token không đúng hoặc đã hết hạn.");
            }
          }}
          className="bg-[var(--color-primary)] text-white px-4 py-2 rounded hover:bg-[#805b42] transition-colors"
        >
          Lưu & Đăng nhập
        </button>
      </div>
    );
  }

  // === UI ===
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6 border-b border-gray-200 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">Trang Quản Trị Cưng Home</h1>
          <button
            onClick={logout}
            className="min-h-10 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Đăng xuất
          </button>
        </div>
        {isUploading && <p role="status" className="mt-3 font-semibold text-blue-600 animate-pulse">⏳ Đang tải file lên...</p>}
        <nav aria-label="Chức năng quản trị" className="mt-5 grid grid-cols-3 gap-2 rounded-xl bg-[#f5efe7] p-1.5">
          {[
            ["banners", "Banner"],
            ["products", "Sản phẩm"],
            ["payments", "Phiếu Cọc"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-current={view === value ? "page" : undefined}
              onClick={() => setView(value)}
              className={`min-h-11 rounded-lg px-2 py-2 text-sm font-semibold transition sm:px-4 ${
                view === value
                  ? "bg-[var(--color-primary)] text-white shadow-sm"
                  : "bg-transparent text-gray-700 hover:bg-white hover:text-[var(--color-primary)]"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      <div hidden={view !== "payments"}>
        <PaymentRequests active={view === "payments"} token={token} onUnauthorized={logout} />
      </div>

      {/* ====== BANNERS ====== */}
      <section hidden={view !== "banners"}>
          <h2 className="font-semibold mb-3">Danh sách Banner</h2>
          <ul className="space-y-2 mb-6">
            {banners.map((b) => (
              <li key={b._id} className="flex justify-between border p-3 rounded">
                <span>{b.title}</span>
                <button
                  onClick={async () => {
                    if (window.confirm("Bạn có chắc chắn muốn xóa banner này?")) {
                      await fetch(`${API}/banners/${b._id}`, {
                        method: "DELETE",
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      fetchBanners();
                    }
                  }}
                  className="text-red-500 hover:underline"
                >
                  Xóa
                </button>
              </li>
            ))}
          </ul>

          <h3 className="font-semibold mb-2">Thêm Banner mới</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Tải ảnh/video lên</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) =>
                  handleSingleUpload(e, (url) =>
                    setNewBanner((current) => ({ ...current, image: url }))
                  )
                }
                className="border px-3 py-1.5 rounded w-full bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Hoặc dán Link file</label>
              <input
                placeholder="Link ảnh"
                value={newBanner.image}
                onChange={(e) =>
                  setNewBanner({ ...newBanner, image: e.target.value })
                }
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          </div>
          {newBanner.image && (
            <MediaPreview url={newBanner.image} alt="Banner preview" className="h-24 w-auto rounded mb-2 border" />
          )}
          <input
            placeholder="Tiêu đề"
            value={newBanner.title}
            onChange={(e) =>
              setNewBanner({ ...newBanner, title: e.target.value })
            }
            className="border px-3 py-2 rounded w-full mb-2"
          />
          <input
            placeholder="Phụ đề"
            value={newBanner.subtitle}
            onChange={(e) =>
              setNewBanner({ ...newBanner, subtitle: e.target.value })
            }
            className="border px-3 py-2 rounded w-full mb-2"
          />
          <textarea
            placeholder="Mô tả"
            value={newBanner.description}
            onChange={(e) =>
              setNewBanner({ ...newBanner, description: e.target.value })
            }
            className="border px-3 py-2 rounded w-full mb-2"
          />
          <button
            onClick={addBanner}
            disabled={isSubmitting || isUploading}
            className={`bg-[var(--color-primary)] hover:bg-[#805b42] transition-colors text-white px-4 py-2 rounded ${
              isSubmitting || isUploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isUploading
              ? "Đang tải ảnh..."
              : isSubmitting
                ? "Đang xử lý..."
                : "+ Thêm Banner"}
          </button>
      </section>

      {/* ====== PRODUCTS ====== */}
      <section hidden={view !== "products"}>


          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">
              {editingProductId ? "Sửa Sản phẩm" : "Thêm Sản phẩm mới"}
            </h3>
            {editingProductId && (
              <button
                onClick={resetProductForm}
                type="button"
                className="border border-gray-300 px-3 py-1 rounded text-sm hover:bg-gray-50"
              >
                Hủy sửa
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <input
              placeholder="Tên sản phẩm"
              value={newProduct.name}
              onChange={(e) =>
                setNewProduct({ ...newProduct, name: e.target.value })
              }
              className="border px-3 py-2 rounded"
            />
            <input
              placeholder="Giá (vd: 12.500.000đ hoặc Liên hệ)"
              value={newProduct.price}
              onChange={(e) => {
                let val = e.target.value;
                // Nếu người dùng nhập toàn số → tự format
                if (/^\d+$/.test(val)) {
                  const formatted = parseInt(val)
                    .toLocaleString("vi-VN")
                    .replace(/,/g, ".") + "đ";
                  setNewProduct({ ...newProduct, price: formatted });
                } else {
                  // Nếu có chữ hoặc ký tự đặc biệt (Liên hệ, khoảng trắng, ...) thì giữ nguyên
                  setNewProduct({ ...newProduct, price: val });
                }
              }}
              className="border px-3 py-2 rounded"
            />


            {/* Dropdown loại sofa */}
            <select
              value={newProduct.type}
              onChange={(e) =>
                setNewProduct({ ...newProduct, type: e.target.value })
              }
              className="border px-3 py-2 rounded"
            >
              <option value="">-- Chọn loại sofa --</option>
              <option value="Sofa L">Sofa L</option>
              <option value="Sofa Băng">Sofa Băng</option>
              <option value="Sofa Bed">Sofa Bed</option>
              <option value="Sofa Da">Sofa Da</option>
              <option value="Ghế">Ghế</option>
              <option value="Đôn">Đôn</option>
              <option value="Giường">Giường</option>
              <option value="Sofa Xưởng">Sofa Xưởng</option>
              <option value="Sofa Cong">Sofa Cong</option>
              <option value="Khác">Khác</option>
            </select>

            <input
              placeholder="Màu sắc"
              value={newProduct.color}
              onChange={(e) =>
                setNewProduct({ ...newProduct, color: e.target.value })
              }
              className="border px-3 py-2 rounded"
            />
            <input
              placeholder="Kích thước"
              value={newProduct.size}
              onChange={(e) =>
                setNewProduct({ ...newProduct, size: e.target.value })
              }
              className="border px-3 py-2 rounded"
            />
            <input
              placeholder="Chất liệu"
              value={newProduct.material}
              onChange={(e) =>
                setNewProduct({ ...newProduct, material: e.target.value })
              }
              className="border px-3 py-2 rounded"
            />
            <div>
              <label className="block text-sm text-gray-600 mb-1">File chính (Tải lên ảnh/video)</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) =>
                  handleSingleUpload(e, (url) =>
                    setNewProduct((current) => ({ ...current, image: url }))
                  )
                }
                className="border px-3 py-1.5 rounded w-full bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Hoặc dán Link file chính</label>
              <input
                placeholder="Link file chính"
                value={newProduct.image}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, image: e.target.value })
                }
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          </div>
          {newProduct.image && (
            <MediaPreview url={newProduct.image} alt="Product preview" className="h-24 w-auto rounded mb-4 border" />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Gallery (Chọn nhiều ảnh/video)</label>
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) =>
                  handleMultipleUpload(e, (urls) =>
                    setNewProduct((current) => ({
                      ...current,
                      gallery: current.gallery ? `${current.gallery}, ${urls}` : urls,
                    }))
                  )
                }
                className="border px-3 py-1.5 rounded w-full bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Hoặc dán Link Gallery</label>
              <textarea
                placeholder="Nhiều link file, cách nhau dấu phẩy"
                value={newProduct.gallery}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, gallery: e.target.value })
                }
                className="border px-3 py-2 rounded w-full h-[38px] overflow-hidden"
              />
            </div>
          </div>
          {newProduct.gallery && (
            <div className="flex gap-2 overflow-x-auto mb-4">
               {newProduct.gallery.split(',').filter(x => x.trim()).map((fileUrl, i) => (
                 <MediaPreview key={i} url={fileUrl.trim()} className="h-16 w-16 object-cover rounded border" />
               ))}
            </div>
          )}

          <section className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="font-semibold text-[var(--color-primary)]">Ảnh thực tế đã xác minh</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Chỉ thêm ảnh sản phẩm đã hoàn thiện hoặc ảnh khách hàng cho phép sử dụng.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-gray-600">Tải nhiều ảnh thực tế</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    handleMultipleUpload(e, (urls) =>
                      setNewProduct((current) => ({
                        ...current,
                        realImages: current.realImages
                          ? `${current.realImages}, ${urls}`
                          : urls,
                      }))
                    )
                  }
                  className="w-full rounded border bg-white px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Hoặc dán link ảnh</label>
                <textarea
                  placeholder="Nhiều link ảnh, cách nhau dấu phẩy"
                  value={newProduct.realImages}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, realImages: e.target.value })
                  }
                  className="h-[38px] w-full overflow-hidden rounded border px-3 py-2"
                />
              </div>
            </div>
            {newProduct.realImages && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {newProduct.realImages
                  .split(",")
                  .filter((item) => item.trim())
                  .map((fileUrl, index) => (
                    <MediaPreview
                      key={`${fileUrl}-${index}`}
                      url={fileUrl.trim()}
                      alt={`Ảnh thực tế ${index + 1}`}
                      className="h-20 w-20 rounded border object-cover"
                    />
                  ))}
              </div>
            )}
          </section>

          <section className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-[var(--color-primary)]">Đánh giá đã xác minh</h3>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Nên kèm link bài đăng, tin nhắn hoặc nguồn phản hồi để có thể đối chiếu.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setNewProduct({
                    ...newProduct,
                    reviews: [
                      ...newProduct.reviews,
                      { name: "", rating: 5, content: "", date: "", sourceUrl: "" },
                    ],
                  })
                }
                className="shrink-0 rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-semibold text-white"
              >
                + Thêm đánh giá
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {newProduct.reviews.length === 0 && (
                <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
                  Chưa có đánh giá. Nhấn “Thêm đánh giá” để nhập phản hồi thật.
                </p>
              )}
              {newProduct.reviews.map((review, index) => (
                <div key={index} className="rounded-md border border-gray-200 bg-white p-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px_160px]">
                    <input
                      placeholder="Tên khách hàng"
                      value={review.name}
                      onChange={(e) => {
                        const reviews = [...newProduct.reviews];
                        reviews[index] = { ...review, name: e.target.value };
                        setNewProduct({ ...newProduct, reviews });
                      }}
                      className="rounded border px-3 py-2"
                    />
                    <select
                      value={review.rating}
                      onChange={(e) => {
                        const reviews = [...newProduct.reviews];
                        reviews[index] = { ...review, rating: Number(e.target.value) };
                        setNewProduct({ ...newProduct, reviews });
                      }}
                      className="rounded border px-3 py-2"
                    >
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <option key={rating} value={rating}>{rating} sao</option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={review.date}
                      onChange={(e) => {
                        const reviews = [...newProduct.reviews];
                        reviews[index] = { ...review, date: e.target.value };
                        setNewProduct({ ...newProduct, reviews });
                      }}
                      className="rounded border px-3 py-2"
                    />
                  </div>
                  <textarea
                    placeholder="Nội dung phản hồi thật"
                    value={review.content}
                    onChange={(e) => {
                      const reviews = [...newProduct.reviews];
                      reviews[index] = { ...review, content: e.target.value };
                      setNewProduct({ ...newProduct, reviews });
                    }}
                    className="mt-3 min-h-24 w-full rounded border px-3 py-2"
                  />
                  <div className="mt-3 flex flex-col gap-3 md:flex-row">
                    <input
                      type="url"
                      placeholder="Link nguồn xác minh (Facebook, Instagram...)"
                      value={review.sourceUrl}
                      onChange={(e) => {
                        const reviews = [...newProduct.reviews];
                        reviews[index] = { ...review, sourceUrl: e.target.value };
                        setNewProduct({ ...newProduct, reviews });
                      }}
                      className="min-w-0 flex-1 rounded border px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setNewProduct({
                          ...newProduct,
                          reviews: newProduct.reviews.filter((_, reviewIndex) => reviewIndex !== index),
                        })
                      }
                      className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600"
                    >
                      Xóa đánh giá
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <textarea
            placeholder="Mô tả sản phẩm"
            value={newProduct.description}
            onChange={(e) =>
              setNewProduct({ ...newProduct, description: e.target.value })
            }
            className="border px-3 py-2 rounded w-full mb-3"
          />

          <h3 className="font-semibold mb-2 text-[var(--color-primary)]">📏 Thông số sản phẩm</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            {/* Dài */}
            <div className="relative">
              <input
                type="number"
                min="0"
                placeholder="Dài"
                value={newProduct.details?.dai ?? DEFAULT_PRODUCT_DETAILS.dai}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    details: { ...newProduct.details, dai: e.target.value },
                  })
                }
                className="border px-3 py-2 rounded w-full pr-10"
              />
              <span className="absolute right-3 top-2 text-gray-500">cm</span>
            </div>

            {/* Rộng */}
            <div className="relative">
              <input
                type="number"
                min="0"
                placeholder="Rộng"
                value={newProduct.details?.rong ?? DEFAULT_PRODUCT_DETAILS.rong}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    details: { ...newProduct.details, rong: e.target.value },
                  })
                }
                className="border px-3 py-2 rounded w-full pr-10"
              />
              <span className="absolute right-3 top-2 text-gray-500">cm</span>
            </div>

            {/* Cao */}
            <div className="relative">
              <input
                type="number"
                min="0"
                placeholder="Cao"
                value={newProduct.details?.cao ?? DEFAULT_PRODUCT_DETAILS.cao}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    details: { ...newProduct.details, cao: e.target.value },
                  })
                }
                className="border px-3 py-2 rounded w-full pr-10"
              />
              <span className="absolute right-3 top-2 text-gray-500">cm</span>
            </div>

            {/* Hình minh họa */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Tải hình/video minh họa</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) =>
                  handleSingleUpload(e, (url) =>
                    setNewProduct((current) => ({
                      ...current,
                      details: { ...current.details, image: url },
                    }))
                  )
                }
                className="border px-3 py-1.5 rounded w-full bg-white text-sm"
              />
              <input
                type="text"
                placeholder="Hoặc dán Link file minh họa"
                value={newProduct.details?.image || ""}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    details: { ...newProduct.details, image: e.target.value },
                  })
                }
                className="border px-3 py-2 rounded w-full mt-2"
              />
              {newProduct.details?.image && (
                <MediaPreview url={newProduct.details.image} className="h-16 mt-2 object-contain" />
              )}
            </div>
          </div>



          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              placeholder="Số lượng tồn kho"
              type="number"
              value={newProduct.stock}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  stock: parseInt(e.target.value) || 0,
                })
              }
              className="border px-3 py-2 rounded"
            />
            <input
              placeholder="Tags (ngăn cách bởi dấu phẩy)"
              value={newProduct.tags}
              onChange={(e) =>
                setNewProduct({ ...newProduct, tags: e.target.value })
              }
              className="border px-3 py-2 rounded"
            />
          </div>

          <div className="flex items-center gap-3 mb-3">
            <input
              type="checkbox"
              checked={newProduct.isFeatured}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  isFeatured: e.target.checked,
                })
              }
            />
            <label>Sản phẩm nổi bật</label>
          </div>

          <button
            onClick={saveProduct}
            disabled={isSubmitting || isUploading}
            className={`bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded ${
              isSubmitting || isUploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isUploading
              ? "Đang tải file..."
              : isSubmitting
                ? "Đang lưu..."
                : editingProductId
                  ? "Lưu thay đổi"
                  : "+ Thêm Sản phẩm"}
          </button>
          <br></br>
          <br></br>
          <br></br>
          <h2 className="font-semibold mb-3">Danh sách Sản phẩm</h2>
          <ul className="space-y-2 mb-6">
            {products.map((p) => (
              <li key={p._id} className="flex justify-between gap-3 border p-3 rounded">
                <span>{p.name}</span>
                <div className="flex shrink-0 items-center gap-3">
                  <button
                    onClick={() => {
                      setEditingProductId(p._id);
                      setNewProduct(productToForm(p));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Sửa
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm(`Xóa sản phẩm "${p.name}"?`)) {
                        await fetch(`${API}/products/${p._id}`, {
                          method: "DELETE",
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (editingProductId === p._id) resetProductForm();
                        fetchProducts();
                      }
                    }}
                    className="text-red-500 hover:underline"
                  >
                    Xóa
                  </button>
                </div>
              </li>
            ))}
          </ul>
      </section>
    </div>
  );
}
